import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../styles/Projects.css';

/* Fallback image */
import img_mountain from '../assets/image/mountain_village.png';

const API_URL = `${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/projects/`;

function ProjectDetails() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPhoto, setCurrentPhoto] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchProject = async () => {
      try {
        const res = await fetch(`${API_URL}${id}/`, {
          headers: {
            'Accept-Language': i18n.language || 'fr'
          }
        });
        if (res.ok) {
          const data = await res.json();
          const images = data.images && data.images.length > 0
            ? data.images.map(imgObj => imgObj.image)
            : [img_mountain];
          setProject({ ...data, images });
        } else {
          // Redirect if not found
          navigate('/projets');
        }
      } catch (err) {
        console.error("Erreur de chargement du projet:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [id, navigate, i18n.language]);

  const getProgress = (status) => {
    const s = (status || '').toLowerCase();
    if (s.includes('terminé') || s.includes('complété') || s.includes('succès')) return 100;
    if (s.includes('cours') || s.includes('déploiement')) return 75;
    return 85;
  };

  if (loading) {
    return (
      <div className="proj-loading-container" style={{ minHeight: '100vh' }}>
        <div className="proj-spinner"></div>
        <p style={{ color: '#666', fontWeight: 600 }}>{t('projects.detailsLoading')}</p>
      </div>
    );
  }

  if (!project) return null;

  const progressVal = getProgress(project.statut);
  const safeImages = project.images && project.images.length > 0 ? project.images : [img_mountain];

  return (
    <div className="projects-page">
      {/* ── HERO BANNER ── */}
      <section
        className="ap-hero"
        style={{ backgroundImage: `url(${img_mountain})` }}
      >
        <div className="ap-hero-overlay"></div>
        <div className="ap-hero-content">
          <span className="ap-hero-badge">{t('projects.badgeHeroPrefix')} {project.statut || t('projects.defaultStatus')}</span>
          <h1 className="ap-hero-title">
            <span className="keyword">{project.titre}</span>
          </h1>
          <p className="ap-hero-sub">
            {t('projects.detailsSubHero')}
          </p>
        </div>
        <div className="ap-hero-wave">
          <svg viewBox="0 0 1440 100" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path
              fill="var(--background-solid)"
              d="M0,100 L0,40 C360,100 1080,0 1440,60 L1440,100 Z"
            ></path>
          </svg>
        </div>
      </section>

      {/* ── MAIN CONTENT ── */}
      <section className="proj-section">
        <div className="proj-container">
          <div className="proj-details-layout">

            {/* Left Column: Gallery & Description */}
            <div className="proj-details-main">
              <Link to="/projets" className="proj-details-back-link">
                {t('projects.backLink')}
              </Link>

              {/* Photos Gallery */}
              <div className="proj-details-gallery">
                <div className="proj-details-main-img-container">
                  <img
                    src={safeImages[currentPhoto]}
                    alt={project.titre}
                    className="proj-details-main-img"
                  />
                </div>

                {/* Thumbnails if multiple photos */}
                {safeImages.length > 1 && (
                  <div className="proj-details-thumbs">
                    {safeImages.map((photo, i) => (
                      <button
                        key={i}
                        className={`proj-details-thumb-btn${i === currentPhoto ? ' active' : ''}`}
                        onClick={() => setCurrentPhoto(i)}
                      >
                        <img src={photo} alt={`Miniature ${i + 1}`} className="proj-details-thumb-img" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="proj-details-content">
                <h2 className="proj-details-section-title">{t('projects.sectionTitle')}</h2>
                <p className="proj-details-description">{project.description}</p>
              </div>
            </div>

            {/* Right Column: Sidebar */}
            <aside className="proj-details-sidebar">
              <div className="proj-details-info-card">
                <h3 className="proj-details-sidebar-title">{t('projects.infoTitle')}</h3>

                <ul className="proj-details-info-list">
                  <li className="proj-details-info-item">
                    <span className="proj-details-info-label">{t('projects.infoStatus')}</span>
                    <span className="proj-details-info-value--status">{project.statut || t('projects.defaultStatus')}</span>
                  </li>
                  <li className="proj-details-info-item">
                    <span className="proj-details-info-label">{t('projects.infoBeneficiaries')}</span>
                    <span className="proj-details-info-value">{project.beneficiaires || t('projects.infoDefaultBeneficiaries')}</span>
                  </li>
                  <li className="proj-details-info-item">
                    <span className="proj-details-info-label">{t('projects.infoLocation')}</span>
                    <span className="proj-details-info-value">{project.lieu_action || t('projects.infoDefaultLocation')}</span>
                  </li>
                  <li className="proj-details-info-item">
                    <span className="proj-details-info-label">{t('projects.infoObjective')}</span>
                    <span className="proj-details-info-value">{project.objectif || t('projects.infoDefaultObjective')}</span>
                  </li>
                  <li className="proj-details-info-item">
                    <span className="proj-details-info-label">{t('projects.infoDate')}</span>
                    <span className="proj-details-info-value">{project.date_lancement || t('projects.infoDefaultDate')}</span>
                  </li>
                </ul>

                <hr className="proj-details-sidebar-divider" />

                {/* Progress bar */}
                <div className="proj-card-impact" style={{ borderTop: 'none', padding: 0 }}>
                  <div className="proj-impact-header" style={{ fontSize: '0.85rem' }}>
                    <span>{t('projects.progressLabel')}</span>
                    <span className="proj-impact-value" style={{ fontSize: '0.95rem' }}>{progressVal}%</span>
                  </div>
                  <div className="proj-progress-bar-bg" style={{ height: '8px' }}>
                    <div
                      className="proj-progress-bar-fill"
                      style={{ width: `${progressVal}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </aside>

          </div>
        </div>
      </section>
    </div>
  );
}

export default ProjectDetails;
